import { CheckIcon, CopyIcon } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Button, type ButtonProps } from '@/components/ui/button'
import { copyToClipboard } from '@/lib/clipboard'
import { cn } from '@/lib/utils'

interface CopyButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  value: string
  /** Accessible label; also the tooltip-ish title. */
  label?: string
  /** Render the word "Copy" next to the icon. */
  withText?: boolean
}

export function CopyButton({
  value,
  label = 'Copy to clipboard',
  withText = false,
  className,
  variant = 'ghost',
  size,
  ...props
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false)
  const [failed, setFailed] = useState(false)
  const timer = useRef<number | undefined>(undefined)

  useEffect(
    () => () => {
      if (timer.current !== undefined) window.clearTimeout(timer.current)
    },
    [],
  )

  const onCopy = useCallback(() => {
    void (async () => {
      const ok = await copyToClipboard(value)
      setCopied(ok)
      setFailed(!ok)
      if (timer.current !== undefined) window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => {
        setCopied(false)
        setFailed(false)
      }, 1800)
    })()
  }, [value])

  return (
    <Button
      type="button"
      variant={variant}
      size={size ?? (withText ? 'sm' : 'icon-sm')}
      onClick={onCopy}
      aria-label={label}
      title={failed ? 'Copy failed — select the text and copy manually' : label}
      className={cn(copied ? 'text-[color-mix(in_oklch,var(--accent)_75%,var(--foreground))]' : '', className)}
      {...props}
    >
      {copied ? <CheckIcon className="size-4" /> : <CopyIcon className="size-4" />}
      {withText ? <span>{copied ? 'Copied' : failed ? 'Copy failed' : 'Copy'}</span> : null}
    </Button>
  )
}
